import type { ReportHeatMapData } from "@repo/app-types/entities/report";
import { skeletonStyle } from "@repo/ui/Skeletons/skeletons";
import { type FC, Fragment, type ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { BaseCard } from "./baseCard.js";
import { renderValueAsString } from "./values.js";

export interface HeatMapCardProps {
  title: ReactNode;
  data?: ReportHeatMapData;
}

function cellColor(
  value: number | null,
  valueType: "passRate" | "calls" | "teammates" | "oneToFiveScoreAverage",
): string {
  if (value === null) return "bg-gray-100 text-muted-foreground";

  if (valueType === "oneToFiveScoreAverage") {
    if (value >= 4.0) return "bg-green-100 text-green-800";
    if (value >= 3.0) return "bg-yellow-100 text-yellow-800";
    if (value >= 2.0) return "bg-orange-100 text-orange-800";
    return "bg-red-100 text-red-800";
  }

  if (value >= 0.97) return "bg-green-100 text-green-800";
  if (value >= 0.93) return "bg-yellow-100 text-yellow-800";
  if (value >= 0.88) return "bg-orange-100 text-orange-800";
  return "bg-red-100 text-red-800";
}

function resolveCellValueType(
  data: ReportHeatMapData,
  row: ReportHeatMapData["rows"][number],
  cell: ReportHeatMapData["rows"][number]["cells"][number],
  colIndex: number,
): "passRate" | "oneToFiveScoreAverage" {
  return (
    cell.valueType ??
    data.columns[colIndex]?.valueType ??
    row.valueType ??
    data.valueType ??
    "passRate"
  );
}

function collectValueTypes(
  data: ReportHeatMapData,
): readonly ("passRate" | "oneToFiveScoreAverage")[] {
  const seen = new Set<"passRate" | "oneToFiveScoreAverage">();
  for (const row of data.rows) {
    for (
      let colIndex = 0;
      colIndex < row.cells.length && colIndex < 4;
      colIndex++
    ) {
      const cell = row.cells[colIndex];
      if (cell === undefined) continue;
      seen.add(resolveCellValueType(data, row, cell, colIndex));
    }
  }
  return [...seen];
}

export const HeatMapCard: FC<HeatMapCardProps> = (props) => {
  const { data } = props;

  return (
    <BaseCard title={props.title}>
      {data ? (
        <>
          {data.columns.length === 0 || data.rows.length === 0 ? (
            <div className="flex flex-auto items-center justify-center py-12 text-center text-sm text-gray-400">
              No data
            </div>
          ) : (
            <>
              <div
                className="my-2 items-center gap-2"
                style={{
                  display: "grid",
                  gridTemplateColumns: `auto repeat(${Math.min(data.columns.length, 4)}, minmax(0, 1fr))`,
                }}
              >
                <div />
                {data.columns.slice(0, 4).map((col, index) => (
                  <div
                    key={index}
                    className="line-clamp-2 self-end overflow-hidden py-1 text-center text-xs font-medium break-words text-ellipsis text-foreground"
                  >
                    {col.label}
                  </div>
                ))}

                {data.rows.map((row, rowIndex) => (
                  <Fragment key={rowIndex}>
                    <div className="max-w-32 truncate pr-1 text-xs font-medium text-foreground">
                      {row.label}
                    </div>
                    {row.cells.slice(0, 4).map((cell, colIndex) => {
                      const valueType = resolveCellValueType(
                        data,
                        row,
                        cell,
                        colIndex,
                      );

                      return (
                        <div
                          key={colIndex}
                          className={twMerge(
                            "rounded-sm p-2 text-center text-xs font-semibold",
                            cellColor(cell.value, valueType),
                          )}
                        >
                          {renderValueAsString({
                            value: cell.value,
                            valueType,
                          })}
                        </div>
                      );
                    })}
                  </Fragment>
                ))}
              </div>

              <div className="my-1 space-y-0.5">
                {collectValueTypes(data).map((valueType) => (
                  <HeatMapLegend key={valueType} valueType={valueType} />
                ))}
              </div>
            </>
          )}
        </>
      ) : (
        <HeatMapSkeleton />
      )}
    </BaseCard>
  );
};

const legendItems: Record<
  "passRate" | "oneToFiveScoreAverage",
  readonly { readonly color: string; readonly label: string }[]
> = {
  passRate: [
    { color: "bg-green-100", label: "\u2265 97%" },
    { color: "bg-yellow-100", label: "93\u201397%" },
    { color: "bg-orange-100", label: "88\u201393%" },
    { color: "bg-red-100", label: "< 88%" },
  ],
  oneToFiveScoreAverage: [
    { color: "bg-green-100", label: "\u2265 4.0" },
    { color: "bg-yellow-100", label: "3.0\u20134.0" },
    { color: "bg-orange-100", label: "2.0\u20133.0" },
    { color: "bg-red-100", label: "< 2.0" },
  ],
};

const HeatMapLegend: FC<{
  valueType: "passRate" | "oneToFiveScoreAverage";
}> = ({ valueType }) => (
  <div className="flex flex-wrap items-center gap-3">
    {legendItems[valueType].map((item) => (
      <div key={item.label} className="flex items-center gap-1">
        <div className={twMerge("size-3 rounded-xs", item.color)} />
        <div className="text-xs text-muted-foreground">{item.label}</div>
      </div>
    ))}
  </div>
);

const HeatMapSkeleton: FC = () => (
  <div className="my-2 animate-pulse space-y-2">
    {Array.from({ length: 5 }).map((_, i) => (
      <div key={i} className="flex gap-2">
        <div className={skeletonStyle({ className: "h-6 w-28" })} />
        <div
          className={skeletonStyle({ className: "h-6 flex-1 rounded-sm" })}
        />
        <div
          className={skeletonStyle({ className: "h-6 flex-1 rounded-sm" })}
        />
        <div
          className={skeletonStyle({ className: "h-6 flex-1 rounded-sm" })}
        />
        <div
          className={skeletonStyle({ className: "h-6 flex-1 rounded-sm" })}
        />
      </div>
    ))}
  </div>
);
